import { useEffect, useState } from "react";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { hasAdminPermission } from "../../contracts/admin-permissions";
import type {
  AdminProjectionFreshness,
  AdminUserLookupInput,
} from "../../contracts/admin-read-models";
import {
  createIdleAdminScreenState,
  createLoadingAdminScreenState,
  type AdminScreenState,
} from "../../contracts/admin-screen-state";
import { useAdminAutoRefreshTick } from "../../shared/query/admin-auto-refresh";
import { createAdminPermissionDeniedState } from "../../shared/query/create-admin-permission-denied-state";
import { normalizeAdminQueryError } from "../../shared/query/normalize-admin-query-error";
import { toAdminScreenErrorState } from "../../shared/query/to-admin-screen-error-state";
import { toUserDataDetailViewModel, type UserDataDetailViewModel } from "./user-data-view-model";
import { classifyUserDataRisk, loadUserDataDetail } from "./users-use-cases";

export type UserDataDetailScreenModelDependencies = AdminDashboardDependencies & {
  locale?: AdminDashboardLocale;
  lookup: AdminUserLookupInput;
};

export type UserDataDetailScreenModel = AdminScreenState<UserDataDetailViewModel>;

function deriveFreshnessPriority(
  sourceFreshness: Record<string, AdminProjectionFreshness>,
): "stale" | "partial" | "ready" {
  const values = Object.values(sourceFreshness);

  if (values.includes("stale")) {
    return "stale";
  }

  if (values.includes("partial") || values.includes("unavailable")) {
    return "partial";
  }

  return "ready";
}

function createDependenciesKey(dependencies: UserDataDetailScreenModelDependencies): string {
  return JSON.stringify({
    roles: dependencies.adminRoles,
    lookup: dependencies.lookup,
  });
}

export async function resolveUserDataDetailScreenModel(
  dependencies: UserDataDetailScreenModelDependencies,
): Promise<UserDataDetailScreenModel> {
  if (!hasAdminPermission(dependencies.adminPermissions, "admin.user_data.read")) {
    return createAdminPermissionDeniedState(
      dependencies.locale ?? "ko",
      {
        en: "User data detail read permission denied.",
        ko: "사용자 데이터 상세 조회 권한이 없습니다.",
      },
      "admin_user_data_permission_denied",
    );
  }

  if (!dependencies.lookup.internalUserId) {
    return {
      status: "empty",
    };
  }

  try {
    const envelope = await loadUserDataDetail(dependencies.queryPort, dependencies.lookup);
    const riskSummary = classifyUserDataRisk(envelope.data);
    const viewModel = toUserDataDetailViewModel(
      envelope.data,
      envelope.meta,
      riskSummary,
      dependencies.locale ?? "ko",
    );
    const freshnessPriority = deriveFreshnessPriority(envelope.meta.sourceFreshness);

    if (freshnessPriority === "stale") {
      return {
        status: "stale",
        viewModel,
      };
    }

    if (freshnessPriority === "partial") {
      return {
        status: "partial",
        viewModel,
      };
    }

    return {
      status: "ready",
      viewModel,
    };
  } catch (error) {
    const normalizedError = normalizeAdminQueryError(error);

    if (normalizedError.code === "ADMIN_QUERY_USER_NOT_FOUND") {
      return {
        status: "empty",
      };
    }

    return toAdminScreenErrorState(dependencies.locale ?? "ko", normalizedError);
  }
}

export function useUserDataDetailScreenModel(
  dependencies: UserDataDetailScreenModelDependencies,
): UserDataDetailScreenModel {
  const [state, setState] = useState<UserDataDetailScreenModel>(createIdleAdminScreenState());
  const dependenciesKey = createDependenciesKey(dependencies);
  const refreshTick = useAdminAutoRefreshTick(dependencies.runtimeConfig);

  useEffect(() => {
    let cancelled = false;

    setState((current) => createLoadingAdminScreenState(current.viewModel));

    void resolveUserDataDetailScreenModel(dependencies).then((nextState) => {
      if (!cancelled) {
        setState(nextState);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dependencies.queryPort, dependenciesKey, refreshTick]);

  return state;
}
